import CONFIG from '../config';

/**
 * API service for communicating with backend
 */

/**
 * Verify Google ID token and get student profile
 */
export async function verifyToken(idToken) {
  let response;
  try {
    if (CONFIG.BACKEND_TYPE === 'apps-script') {
      response = await fetch(CONFIG.BACKEND_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'verifyToken',
          idToken: idToken,
        }),
      });
    } else {
      response = await fetch(`${CONFIG.BACKEND_URL}/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idToken: idToken,
        }),
      });
    }
    
    // Check if response is ok
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    // If it's already an Error with a message, re-throw it
    if (error instanceof Error) {
      throw error;
    }
    // Otherwise, wrap it in an Error
    throw new Error(error.message || 'Network error: Could not connect to server');
  }
}

/**
 * Get student statistics
 */
export async function getStudentStats(studentId) {
  if (CONFIG.BACKEND_TYPE === 'apps-script') {
    return fetch(CONFIG.BACKEND_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getStudentStats',
        studentId: studentId,
      }),
    }).then(res => res.json());
  } else {
    return fetch(`${CONFIG.BACKEND_URL}/students/${encodeURIComponent(studentId)}/stats`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(res => res.json());
  }
}

/**
 * Submit reading answer (Yes or No)
 */
export async function submitAnswer(studentId, answer) {
  if (CONFIG.BACKEND_TYPE === 'apps-script') {
    return fetch(CONFIG.BACKEND_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'submitAnswer',
        studentId: studentId,
        answer: answer,
      }),
    }).then(res => res.json());
  } else {
    return fetch(`${CONFIG.BACKEND_URL}/students/${encodeURIComponent(studentId)}/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        answer: answer,
      }),
    }).then(res => res.json());
  }
}

/**
 * Get group totals
 */
export async function getGroupTotals(groupName) {
  if (CONFIG.BACKEND_TYPE === 'apps-script') {
    return fetch(CONFIG.BACKEND_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getGroupTotals',
        groupName: groupName,
      }),
    }).then(res => res.json());
  } else {
    return fetch(`${CONFIG.BACKEND_URL}/groups/${encodeURIComponent(groupName)}/totals`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(res => res.json());
  }
}

/**
 * Get all groups' totals (for leaderboard)
 */
export async function getAllGroupsTotals() {
  if (CONFIG.BACKEND_TYPE === 'apps-script') {
    // For Apps Script, we'd need to add this action
    return fetch(CONFIG.BACKEND_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getAllGroupsTotals',
      }),
    }).then(res => res.json());
  } else {
    return fetch(`${CONFIG.BACKEND_URL}/groups/all`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(res => res.json());
  }
}

